import * as React from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useSpring, animated, useTransition, config } from '@react-spring/web';

import Popover from '@mui/material/Popover';
import MDBox from 'components/MDBox';
import Icon from '@mui/material/Icon';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDBadge from 'components/MDBadge';
import { getStockPrice } from 'services/outside/aaro/priceList';
import { todayIsBetween } from 'helpers/dateHelpers';
import { formatPrice, calculateDiscount } from 'helpers/priceHelpers';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';

import DetailedStockView from 'layouts/aaro/fastPrice/views/detailedStockView';

const fetchAaroStockPrice = async (stokID) => {
    const response = await getStockPrice(stokID).then((rp) => rp.data);
    return response;
};

export default function MouseOverPopover({ stockID, stockName, stockCode, queryID, objectID }) {
    const queryClient = useQueryClient();
    const [openModal, setOpenModal] = React.useState(false);
    const [shouldFetch, setShouldFetch] = React.useState(false);

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setShouldFetch(true);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const modalSx = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '60%',
        maxWidth: 1000,
        maxHeight: 800,
        bgcolor: 'background.paper',
        border: '2px solid #fff',
        borderRadius: '25px',
        boxShadow: 24,
        p: 4,
        overflowY: 'auto',
        '@media (max-width: 480px)': {
            width: '90%',
        },
    };

    return (
        <MDBox
            onMouseEnter={async () => {
                // Prefetch the data when mouse enters
                await queryClient.prefetchQuery({
                    queryKey: ['fetchAaroStockPrice', stockID],
                    queryFn: () => fetchAaroStockPrice(stockID),
                    staleTime: 60 * 1000,
                });
            }}
            display="flex"
            justifyContent="center"
            alignItems="center"
        >
            <MDButton
                size="medium"
                variant="gradient"
                color="info"
                aria-owns={open ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
                iconOnly
                onClick={() => setOpenModal(!openModal)}
            >
                <Icon>currency_lira</Icon>
            </MDButton>

            <Popover
                id="mouse-over-popover"
                sx={{
                    pointerEvents: 'none',
                }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
            >
                <PriceView stockID={stockID} shouldFetch={shouldFetch} />
            </Popover>
            <Modal
                open={openModal}
                onClose={() => setOpenModal(false)}
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description"
            >
                <Box sx={modalSx}>
                    <DetailedStockView
                        stockID={stockID}
                        activeStockName={stockID}
                        activeStockCode={stockID}
                        setOpen={setOpenModal}
                        stockName={stockName}
                        stockCode={stockCode}
                        queryID={queryID}
                        objectID={objectID}
                    />
                </Box>
            </Modal>
        </MDBox>
    );
}

const PriceView = ({ stockID, shouldFetch }) => {
    // Use useQuery to reactively get data from cache or fetch if needed
    const { data, isLoading } = useQuery({
        queryKey: ['fetchAaroStockPrice', stockID],
        queryFn: () => fetchAaroStockPrice(stockID),
        enabled: shouldFetch, // Only enable when we actually need the data
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const priceDetails = data?.Model ? data.Model[0] : {};
    const isValid = todayIsBetween(new Date(priceDetails?.TarihBas), new Date(priceDetails?.TarihBit));

    // Loading animation - pulsing effect
    const loadingAnimation = useSpring({
        opacity: isLoading ? 1 : 0,
        scale: isLoading ? 1 : 0,
        config: config.wobbly,
    });

    // Pulsing animation for loading text
    const pulseAnimation = useSpring({
        from: { opacity: 0.5, scale: 0.95 },
        to: async (next) => {
            while (isLoading) {
                await next({ opacity: 1, scale: 1 });
                await next({ opacity: 0.5, scale: 0.95 });
            }
        },
        config: config.slow,
    });

    // Content animation - fade in when data is ready
    const contentAnimation = useSpring({
        opacity: !isLoading && data ? 1 : 0,
        transform: !isLoading && data ? 'translateY(0px)' : 'translateY(10px)',
        config: config.gentle,
    });

    // Price badges animation with stagger effect
    const badgeTransitions = useTransition(!isLoading && data && isValid ? [1, 2, 3] : [], {
        from: { opacity: 0, transform: 'scale(0.8) translateY(20px)' },
        enter: { opacity: 1, transform: 'scale(1) translateY(0px)' },
        leave: { opacity: 0, transform: 'scale(0.8) translateY(-20px)' },
        config: config.gentle,
        trail: 100, // Stagger delay between badges
    });

    // Background color animation
    const backgroundAnimation = useSpring({
        backgroundColor: isLoading
            ? '#1976d2' // primary blue
            : isValid
            ? '#2e7d32' // success green
            : '#1976d2', // primary blue
        config: config.gentle,
    });

    if (isLoading || !data) {
        return (
            <animated.div style={loadingAnimation}>
                <MDBox
                    component={animated.div}
                    style={backgroundAnimation}
                    color="white"
                    variant="gradient"
                    borderRadius="lg"
                    shadow="lg"
                    opacity={1}
                    p={2}
                >
                    <animated.div style={pulseAnimation}>
                        <MDTypography variant="h5" color="white">
                            Fiyat Aranıyor...
                        </MDTypography>
                    </animated.div>
                </MDBox>
            </animated.div>
        );
    }

    return (
        <animated.div style={contentAnimation}>
            <MDBox
                component={animated.div}
                style={backgroundAnimation}
                color="white"
                variant="gradient"
                borderRadius="lg"
                shadow="lg"
                opacity={1}
                p={2}
            >
                {isValid ? (
                    <>
                        <animated.div style={contentAnimation}>
                            <MDBox display="flex" justifyContent="center" p={1}>
                                <MDTypography variant="h5" color="white">
                                    {priceDetails.Fiyat && formatPrice(priceDetails.Fiyat, priceDetails.DovizKodu)}
                                </MDTypography>
                                <MDTypography variant="h6" fontWeight="light" color="white">
                                    &nbsp;&nbsp;&nbsp;
                                    {priceDetails.Fiyat && '/' + priceDetails.BirimKodu + ' + K.D.V.'}
                                </MDTypography>
                            </MDBox>
                        </animated.div>

                        <MDBox display="flex" flexWrap="wrap" gap={1} justifyContent="center">
                            {badgeTransitions((style, item) => {
                                const discounts = [
                                    { percent: 10, color: 'info' },
                                    { percent: 15, color: 'warning' },
                                    { percent: 22, color: 'primary' },
                                ];
                                const discount = discounts[item - 1];

                                return (
                                    <animated.div style={style}>
                                        <MDBadge
                                            variant="gradient"
                                            color={discount.color}
                                            badgeContent={`%${discount.percent} - ${formatPrice(
                                                calculateDiscount(priceDetails.Fiyat, 1, discount.percent),
                                                priceDetails.DovizKodu
                                            )}`}
                                            container
                                        />
                                    </animated.div>
                                );
                            })}
                        </MDBox>
                    </>
                ) : (
                    <animated.div style={contentAnimation}>
                        <MDTypography variant="h5" color="white">
                            Fiyat Aranıyor...
                        </MDTypography>
                    </animated.div>
                )}
            </MDBox>
        </animated.div>
    );
};
